import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Chip,
  TextField,
  MenuItem,
  Stack,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
  CameraAlt as CameraAltIcon,
  Star as StarIcon,
  Computer as ComputerIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  DynamicFeed as BulkIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';
import { formatBytes } from '../utils/formatters';

const formatISODate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const GoldenImagesPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [goldenImages, setGoldenImages] = useState([]);
  const [error, setError] = useState(null);

  // OS Image에서 생성 다이얼로그
  const [fromOSOpen, setFromOSOpen] = useState(false);
  const [osImages, setOsImages] = useState([]);
  const [fromOSForm, setFromOSForm] = useState({ image_name: '', display_name: '', description: '', tags: '' });
  const [fromOSLoading, setFromOSLoading] = useState(false);

  // VM에서 캡처 다이얼로그
  const [fromVMOpen, setFromVMOpen] = useState(false);
  const [stoppedVMs, setStoppedVMs] = useState([]);
  const [fromVMForm, setFromVMForm] = useState({ vm_name: '', display_name: '', description: '', tags: '' });
  const [fromVMLoading, setFromVMLoading] = useState(false);

  // 삭제 확인 다이얼로그
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);

  // 메타데이터 수정 다이얼로그
  const [editOpen, setEditOpen] = useState(false);
  const [imageToEdit, setImageToEdit] = useState(null);
  const [editForm, setEditForm] = useState({ display_name: '', description: '', tags: '' });
  const [editLoading, setEditLoading] = useState(false);

  // 일괄 생성 다이얼로그
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkImage, setBulkImage] = useState(null);
  const [bulkNetworks, setBulkNetworks] = useState([]);
  const [bulkCloudInitIsos, setBulkCloudInitIsos] = useState([]);
  const [bulkAssignableGPUs, setBulkAssignableGPUs] = useState([]);
  const [bulkPresets, setBulkPresets] = useState([]);
  const [selectedBulkPreset, setSelectedBulkPreset] = useState('');
  const [bulkForm, setBulkForm] = useState({
    count: 3,
    base_name: '',
    name_suffix_start: 1,
    memory: 4096,
    vcpus: 2,
    disk_size: 20,
    network_profile: 'default',
    username: 'inugpu',
    password: 'gpuinu!!',
  });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null); // null=입력중, {...}=결과
  const [bulkPortForwards, setBulkPortForwards] = useState([]); // [{protocol, vm_port, description}]
  const [bulkPFInput, setBulkPFInput] = useState({ protocol: 'tcp', vm_port: '', description: '' });

  const fetchGoldenImages = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getGoldenImages();
      setGoldenImages(response.data.golden_images || []);
    } catch (err) {
      setError('Golden Image 목록을 불러오는 중 오류가 발생했습니다.');
      enqueueSnackbar('Golden Image 목록을 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGoldenImages();
  }, []);

  // 일괄 생성 열기
  const handleBulkOpen = async (image) => {
    setBulkImage(image);
    setBulkResults(null);
    const minDisk = Math.max(1, Math.ceil((image.virtual_size || image.size || 0) / (1024 ** 3)));
    setBulkForm({
      count: 3,
      base_name: image.display_name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, ''),
      name_suffix_start: 1,
      memory: 4096,
      vcpus: 2,
      disk_size: minDisk,
      network_profile: 'default',
      username: 'inugpu',
      password: 'gpuinu!!',
      ssh_authorized_keys: '',
      cloud_init_iso: '',
      gpu_count: 0,
    });
    try {
      const [netRes, isoRes, gpuRes, presetRes] = await Promise.all([
        apiService.getNetworks(),
        apiService.getCloudInitIsos(),
        apiService.getBulkAssignableGPUs(),
        apiService.getHardwarePresets(),
      ]);
      setBulkNetworks(netRes.data.networks || []);
      setBulkCloudInitIsos(isoRes.data.isos || []);
      setBulkAssignableGPUs(gpuRes.data.gpus || []);
      setBulkPresets(presetRes.data.presets || []);

      // 기본 프리셋이 있으면 자동 선택
      const presets = presetRes.data.presets || [];
      const defaultPreset = presets.find(p => p.is_default);
      if (defaultPreset) {
        setSelectedBulkPreset(defaultPreset.id.toString());
        const minDisk = Math.max(
          Math.max(1, Math.ceil((image.virtual_size || image.size || 0) / (1024 ** 3))),
          defaultPreset.disk_size
        );
        setBulkForm(f => ({
          ...f,
          memory: defaultPreset.memory,
          vcpus: defaultPreset.vcpus,
          disk_size: minDisk,
          network_profile: defaultPreset.network_profile,
        }));
        setBulkPortForwards(
          (defaultPreset.port_forwarding_rules || []).map(r => ({
            protocol: r.protocol,
            vm_port: r.vm_port,
            description: r.description || null,
          }))
        );
      } else {
        setSelectedBulkPreset('');
        setBulkPortForwards([]);
      }
    } catch {
      setBulkNetworks([]);
      setBulkCloudInitIsos([]);
      setBulkAssignableGPUs([]);
      setBulkPresets([]);
      setSelectedBulkPreset('');
      setBulkPortForwards([]);
    }
    setBulkPFInput({ protocol: 'tcp', vm_port: '', description: '' });
    setBulkOpen(true);
  };

  const handleBulkClose = () => {
    setBulkOpen(false);
    setBulkImage(null);
    setBulkResults(null);
    setBulkPortForwards([]);
    setBulkPFInput({ protocol: 'tcp', vm_port: '', description: '' });
    setSelectedBulkPreset('');
    setBulkPresets([]);
  };

  const handleBulkPresetChange = (presetId) => {
    setSelectedBulkPreset(presetId);
    if (!presetId) return;
    const preset = bulkPresets.find(p => p.id.toString() === presetId);
    if (!preset) return;
    const minDisk = Math.max(
      Math.max(1, Math.ceil((bulkImage?.virtual_size || bulkImage?.size || 0) / (1024 ** 3))),
      preset.disk_size
    );
    setBulkForm(f => ({
      ...f,
      memory: preset.memory,
      vcpus: preset.vcpus,
      disk_size: minDisk,
      network_profile: preset.network_profile,
    }));
    setBulkPortForwards(
      (preset.port_forwarding_rules || []).map(r => ({
        protocol: r.protocol,
        vm_port: r.vm_port,
        description: r.description || null,
      }))
    );
    enqueueSnackbar(`VM 프리셋 "${preset.name}" 적용됨`, { variant: 'info' });
  };

  const handleAddPortForward = () => {
    const port = Number(bulkPFInput.vm_port);
    if (!port || port < 1 || port > 65535) {
      enqueueSnackbar('유효한 VM 포트 번호를 입력하세요 (1-65535)', { variant: 'warning' });
      return;
    }
    if (bulkPortForwards.some(p => p.protocol === bulkPFInput.protocol && p.vm_port === port)) {
      enqueueSnackbar('동일한 프로토콜/포트가 이미 추가되어 있습니다.', { variant: 'warning' });
      return;
    }
    setBulkPortForwards(prev => [...prev, {
      protocol: bulkPFInput.protocol,
      vm_port: port,
      description: bulkPFInput.description.trim() || null,
    }]);
    setBulkPFInput(p => ({ ...p, vm_port: '', description: '' }));
  };

  const previewNames = () => {
    if (!bulkForm.base_name) return [];
    return Array.from({ length: Math.min(bulkForm.count, 5) }, (_, i) =>
      `${bulkForm.base_name}-${bulkForm.name_suffix_start + i}`
    );
  };

  const getBulkMinDiskGb = () =>
    Math.max(1, Math.ceil((bulkImage?.virtual_size || bulkImage?.size || 0) / (1024 ** 3)));

  const handleBulkSubmit = async () => {
    if (!bulkForm.base_name.trim()) {
      enqueueSnackbar('기본 이름을 입력해주세요.', { variant: 'warning' });
      return;
    }
    const minDiskGb = getBulkMinDiskGb();
    if (Number(bulkForm.disk_size) < minDiskGb) {
      enqueueSnackbar(`디스크 크기(${bulkForm.disk_size}G)가 Golden Image 가상 디스크 크기(${minDiskGb}G)보다 작습니다.`, { variant: 'warning' });
      return;
    }
    setBulkLoading(true);
    try {
      const sshKeys = (bulkForm.ssh_authorized_keys || '')
        .split('\n')
        .map(k => k.trim())
        .filter(Boolean);
      const res = await apiService.bulkCreateVMs({
        golden_image_id: bulkImage.id,
        ...bulkForm,
        count: Number(bulkForm.count),
        name_suffix_start: Number(bulkForm.name_suffix_start),
        memory: Number(bulkForm.memory),
        vcpus: Number(bulkForm.vcpus),
        disk_size: Number(bulkForm.disk_size),
        ssh_authorized_keys: sshKeys,
        cloud_init_iso: bulkForm.cloud_init_iso || null,
        gpu_count: Number(bulkForm.gpu_count),
        port_forwards: bulkPortForwards,
      });
      setBulkResults(res.data);
      fetchGoldenImages();
      setTimeout(() => {
        setBulkOpen(false);
        setBulkImage(null);
        setBulkResults(null);
        setBulkPortForwards([]);
        setBulkPFInput({ protocol: 'tcp', vm_port: '', description: '' });
      }, 5000);
    } catch (err) {
      enqueueSnackbar(
        `일괄 생성 실패: ${err.response?.data?.detail || err.message}`,
        { variant: 'error' }
      );
    } finally {
      setBulkLoading(false);
    }
  };

  // OS Image에서 생성 열기
  const handleFromOSOpen = async () => {
    try {
      const response = await apiService.getOSImages();
      setOsImages(response.data.images || []);
    } catch {
      enqueueSnackbar('OS 이미지 목록을 불러오는데 실패했습니다.', { variant: 'error' });
    }
    setFromOSForm({ image_name: '', display_name: '', description: '', tags: '' });
    setFromOSOpen(true);
  };

  const handleFromOSSubmit = async () => {
    if (!fromOSForm.image_name || !fromOSForm.display_name) {
      enqueueSnackbar('이미지와 표시 이름을 입력해주세요.', { variant: 'warning' });
      return;
    }
    setFromOSLoading(true);
    try {
      const tags = fromOSForm.tags
        ? fromOSForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];
      await apiService.createGoldenImageFromOS(fromOSForm.image_name, {
        display_name: fromOSForm.display_name,
        description: fromOSForm.description,
        tags,
      });
      enqueueSnackbar('Golden Image가 생성되었습니다.', { variant: 'success' });
      setFromOSOpen(false);
      fetchGoldenImages();
    } catch (err) {
      enqueueSnackbar(
        `Golden Image 생성 실패: ${err.response?.data?.detail || err.message}`,
        { variant: 'error' }
      );
    } finally {
      setFromOSLoading(false);
    }
  };

  // VM에서 캡처 열기
  const handleFromVMOpen = async () => {
    try {
      const response = await apiService.getVMs(true); // all=true: 중지된 VM 포함
      const vms = response.data.vms || [];
      setStoppedVMs(vms.filter(vm => vm.state === 5)); // 5 = VIR_DOMAIN_SHUTOFF
    } catch {
      enqueueSnackbar('VM 목록을 불러오는데 실패했습니다.', { variant: 'error' });
    }
    setFromVMForm({ vm_name: '', display_name: '', description: '', tags: '' });
    setFromVMOpen(true);
  };

  const handleFromVMSubmit = async () => {
    if (!fromVMForm.vm_name || !fromVMForm.display_name) {
      enqueueSnackbar('VM과 표시 이름을 입력해주세요.', { variant: 'warning' });
      return;
    }
    setFromVMLoading(true);
    try {
      const tags = fromVMForm.tags
        ? fromVMForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];
      await apiService.createGoldenImageFromVM(fromVMForm.vm_name, {
        display_name: fromVMForm.display_name,
        description: fromVMForm.description,
        tags,
      });
      enqueueSnackbar('Golden Image가 캡처되었습니다.', { variant: 'success' });
      setFromVMOpen(false);
      fetchGoldenImages();
    } catch (err) {
      enqueueSnackbar(
        `Golden Image 캡처 실패: ${err.response?.data?.detail || err.message}`,
        { variant: 'error' }
      );
    } finally {
      setFromVMLoading(false);
    }
  };

  // 삭제
  const handleDeleteClick = (image) => {
    setImageToDelete(image);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!imageToDelete) return;
    try {
      await apiService.deleteGoldenImage(imageToDelete.id);
      enqueueSnackbar(`Golden Image "${imageToDelete.display_name}"이 삭제되었습니다.`, { variant: 'success' });
      fetchGoldenImages();
    } catch (err) {
      enqueueSnackbar(
        `삭제 실패: ${err.response?.data?.detail || err.message}`,
        { variant: 'error' }
      );
    } finally {
      setDeleteOpen(false);
      setImageToDelete(null);
    }
  };

  // 수정
  const handleEditClick = (image) => {
    setImageToEdit(image);
    setEditForm({
      display_name: image.display_name,
      description: image.description,
      tags: (image.tags || []).join(', '),
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editForm.display_name) {
      enqueueSnackbar('표시 이름을 입력해주세요.', { variant: 'warning' });
      return;
    }
    setEditLoading(true);
    try {
      const tags = editForm.tags
        ? editForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];
      await apiService.updateGoldenImage(imageToEdit.id, {
        display_name: editForm.display_name,
        description: editForm.description,
        tags,
      });
      enqueueSnackbar('Golden Image 정보가 수정되었습니다.', { variant: 'success' });
      setEditOpen(false);
      fetchGoldenImages();
    } catch (err) {
      enqueueSnackbar(
        `수정 실패: ${err.response?.data?.detail || err.message}`,
        { variant: 'error' }
      );
    } finally {
      setEditLoading(false);
    }
  };

  // VM 생성 페이지로 이동 (golden_image_id 전달)
  const handleCreateVM = (image) => {
    navigate('/vms/create', { state: { golden_image_id: image.id, golden_image_name: image.display_name } });
  };

  if (loading && goldenImages.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">Golden Image 관리</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={handleFromOSOpen}
          >
            OS 이미지에서 지정
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<CameraAltIcon />}
            onClick={handleFromVMOpen}
          >
            VM에서 캡처
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchGoldenImages}
          >
            새로고침
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>이름</TableCell>
                <TableCell>설명</TableCell>
                <TableCell>태그</TableCell>
                <TableCell>소스</TableCell>
                <TableCell>크기</TableCell>
                <TableCell>생성일</TableCell>
                <TableCell align="center">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {goldenImages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      등록된 Golden Image가 없습니다.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                goldenImages.map((image) => (
                  <TableRow key={image.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StarIcon fontSize="small" color="warning" />
                        <Typography variant="body2" fontWeight="bold">
                          {image.display_name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {image.filename}
                      </Typography>
                    </TableCell>
                    <TableCell>{image.description || '-'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {(image.tags || []).map(tag => (
                          <Chip key={tag} label={tag} size="small" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {image.source_type === 'vm' ? <ComputerIcon fontSize="small" /> : null}
                        <Typography variant="caption">
                          {image.source_type === 'os_image' ? 'OS: ' : 'VM: '}
                          {image.source_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatBytes(image.size)}</TableCell>
                    <TableCell>{formatISODate(image.created_at)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="VM 생성">
                        <IconButton color="primary" onClick={() => handleCreateVM(image)}>
                          <PlayArrowIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="일괄 VM 생성">
                        <IconButton color="secondary" onClick={() => handleBulkOpen(image)}>
                          <BulkIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="정보 수정">
                        <IconButton color="default" onClick={() => handleEditClick(image)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="삭제">
                        <IconButton color="error" onClick={() => handleDeleteClick(image)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* OS 이미지에서 지정 다이얼로그 */}
      <Dialog open={fromOSOpen} onClose={() => setFromOSOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>OS 이미지에서 Golden Image 지정</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              required
              fullWidth
              select
              label="OS 이미지 선택"
              value={fromOSForm.image_name}
              onChange={e => setFromOSForm(f => ({ ...f, image_name: e.target.value }))}
            >
              {osImages.map(img => (
                <MenuItem key={img.name} value={img.name}>
                  {img.name} ({formatBytes(img.size)})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              fullWidth
              label="표시 이름"
              value={fromOSForm.display_name}
              onChange={e => setFromOSForm(f => ({ ...f, display_name: e.target.value }))}
              placeholder="예: Ubuntu 22.04 CUDA Base"
            />
            <TextField
              fullWidth
              label="설명"
              value={fromOSForm.description}
              onChange={e => setFromOSForm(f => ({ ...f, description: e.target.value }))}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="태그 (쉼표로 구분)"
              value={fromOSForm.tags}
              onChange={e => setFromOSForm(f => ({ ...f, tags: e.target.value }))}
              placeholder="예: ubuntu, cuda, ml"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFromOSOpen(false)} color="inherit" disabled={fromOSLoading}>
            취소
          </Button>
          <Button
            onClick={handleFromOSSubmit}
            variant="contained"
            disabled={fromOSLoading}
            startIcon={fromOSLoading ? <CircularProgress size={20} /> : null}
          >
            {fromOSLoading ? '복사 중...' : '지정'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* VM에서 캡처 다이얼로그 */}
      <Dialog open={fromVMOpen} onClose={() => setFromVMOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>VM 디스크에서 Golden Image 캡처</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              중지된 VM만 캡처할 수 있습니다.
            </Alert>
            <TextField
              required
              fullWidth
              select
              label="중지된 VM 선택"
              value={fromVMForm.vm_name}
              onChange={e => setFromVMForm(f => ({ ...f, vm_name: e.target.value }))}
            >
              {stoppedVMs.length === 0 ? (
                <MenuItem value="" disabled>중지된 VM이 없습니다</MenuItem>
              ) : (
                stoppedVMs.map(vm => (
                  <MenuItem key={vm.name} value={vm.name}>
                    {vm.name}
                  </MenuItem>
                ))
              )}
            </TextField>
            <TextField
              required
              fullWidth
              label="표시 이름"
              value={fromVMForm.display_name}
              onChange={e => setFromVMForm(f => ({ ...f, display_name: e.target.value }))}
              placeholder="예: My Custom Ubuntu 22.04"
            />
            <TextField
              fullWidth
              label="설명"
              value={fromVMForm.description}
              onChange={e => setFromVMForm(f => ({ ...f, description: e.target.value }))}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="태그 (쉼표로 구분)"
              value={fromVMForm.tags}
              onChange={e => setFromVMForm(f => ({ ...f, tags: e.target.value }))}
              placeholder="예: ubuntu, custom"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFromVMOpen(false)} color="inherit" disabled={fromVMLoading}>
            취소
          </Button>
          <Button
            onClick={handleFromVMSubmit}
            variant="contained"
            disabled={fromVMLoading || stoppedVMs.length === 0}
            startIcon={fromVMLoading ? <CircularProgress size={20} /> : null}
          >
            {fromVMLoading ? '캡처 중...' : '캡처'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 수정 다이얼로그 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Golden Image 정보 수정</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              required
              fullWidth
              label="표시 이름"
              value={editForm.display_name}
              onChange={e => setEditForm(f => ({ ...f, display_name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="설명"
              value={editForm.description}
              onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="태그 (쉼표로 구분)"
              value={editForm.tags}
              onChange={e => setEditForm(f => ({ ...f, tags: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} color="inherit" disabled={editLoading}>
            취소
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={20} /> : null}
          >
            {editLoading ? '저장 중...' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 일괄 생성 다이얼로그 */}
      <Dialog open={bulkOpen} onClose={bulkLoading ? undefined : handleBulkClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BulkIcon color="secondary" />
          일괄 VM 생성
        </DialogTitle>
        <DialogContent>
          {bulkResults ? (
            /* 결과 화면 */
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                5초 후 자동으로 닫힙니다.
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`성공 ${bulkResults.success}개`}
                  color="success"
                  variant="outlined"
                />
                {bulkResults.failed > 0 && (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`실패 ${bulkResults.failed}개`}
                    color="error"
                    variant="outlined"
                  />
                )}
                {bulkResults.gpu_requested > 0 && (
                  <Chip
                    label={`GPU 할당 ${bulkResults.gpu_assigned}/${bulkResults.gpu_requested}개`}
                    color={bulkResults.gpu_assigned < bulkResults.gpu_requested ? 'warning' : 'default'}
                    variant="outlined"
                  />
                )}
                {bulkResults.port_forward_rules_created > 0 && (
                  <Chip
                    label={`포트 포워딩 ${bulkResults.port_forward_rules_created}개 등록`}
                    color="info"
                    variant="outlined"
                  />
                )}
              </Stack>
              <List dense disablePadding>
                {bulkResults.results.map((r) => (
                  <ListItem key={r.name} disablePadding sx={{ py: 0.3, flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {r.status === 'success'
                          ? <CheckCircleIcon color="success" fontSize="small" />
                          : <ErrorIcon color="error" fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <span>
                            {r.name}
                            {r.gpu_id && <Chip size="small" label={`GPU: ${r.gpu_id}`} sx={{ ml: 1 }} />}
                          </span>
                        }
                        secondary={r.error || null}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption', color: 'error' }}
                      />
                    </Box>
                    {r.port_forwards?.length > 0 && (
                      <Box sx={{ pl: 4, pb: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {r.port_forwards.map((pf, idx) => (
                          <Chip
                            key={idx}
                            size="small"
                            label={
                              pf.status === 'success'
                                ? `${pf.protocol.toUpperCase()} ${pf.host_port}→:${pf.vm_port}${pf.description ? ` (${pf.description})` : ''}`
                                : `${pf.protocol?.toUpperCase()} :${pf.vm_port} — ${pf.reason || pf.status}`
                            }
                            color={pf.status === 'success' ? 'success' : 'warning'}
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            /* 입력 화면 */
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="info" sx={{ py: 0.5 }}>
                <strong>{bulkImage?.display_name}</strong> 이미지 기반으로 VM을 일괄 생성합니다.
              </Alert>

              {/* VM 프리셋 선택 */}
              <TextField
                fullWidth
                select
                label="VM 프리셋 (선택)"
                value={selectedBulkPreset}
                onChange={e => handleBulkPresetChange(e.target.value)}
                helperText="프리셋 선택 시 하드웨어 설정과 포트 포워딩 규칙이 자동 적용됩니다"
              >
                <MenuItem value=""><em>직접 설정</em></MenuItem>
                {bulkPresets.map(p => (
                  <MenuItem key={p.id} value={p.id.toString()}>
                    {p.name}
                    {p.is_default ? ' (기본)' : ''}
                    {' — '}
                    {(p.memory / 1024).toFixed(1)}GB RAM, {p.vcpus}vCPU, {p.disk_size}GB
                    {(p.port_forwarding_rules || []).length > 0
                      ? ` | 포트포워딩 ${p.port_forwarding_rules.length}개`
                      : ''}
                  </MenuItem>
                ))}
              </TextField>

              <Divider />

              <Stack direction="row" spacing={2}>
                <TextField
                  required
                  label="기본 이름"
                  value={bulkForm.base_name}
                  onChange={e => setBulkForm(f => ({ ...f, base_name: e.target.value }))}
                  placeholder="예: worker"
                  fullWidth
                  helperText="VM 이름 = 기본이름-번호"
                />
                <TextField
                  required
                  label="VM 개수"
                  type="number"
                  value={bulkForm.count}
                  onChange={e => setBulkForm(f => ({ ...f, count: e.target.value }))}
                  inputProps={{ min: 1, max: 20 }}
                  sx={{ width: 110 }}
                />
                <TextField
                  label="시작 번호"
                  type="number"
                  value={bulkForm.name_suffix_start}
                  onChange={e => setBulkForm(f => ({ ...f, name_suffix_start: e.target.value }))}
                  inputProps={{ min: 0 }}
                  sx={{ width: 110 }}
                />
              </Stack>

              {/* 이름 미리보기 */}
              {bulkForm.base_name && (
                <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 2, py: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    생성될 VM 이름 미리보기:&nbsp;
                    {previewNames().join(', ')}
                    {bulkForm.count > 5 ? ` ... (총 ${bulkForm.count}개)` : ''}
                  </Typography>
                </Box>
              )}

              <Divider />

              <Stack direction="row" spacing={2}>
                <TextField
                  required
                  label="메모리 (MB)"
                  type="number"
                  value={bulkForm.memory}
                  onChange={e => setBulkForm(f => ({ ...f, memory: e.target.value }))}
                  inputProps={{ min: 1024, step: 1024 }}
                  fullWidth
                />
                <TextField
                  required
                  label="vCPU"
                  type="number"
                  value={bulkForm.vcpus}
                  onChange={e => setBulkForm(f => ({ ...f, vcpus: e.target.value }))}
                  inputProps={{ min: 1 }}
                  fullWidth
                />
                <TextField
                  required
                  label="디스크 (GB)"
                  type="number"
                  value={bulkForm.disk_size}
                  onChange={e => setBulkForm(f => ({ ...f, disk_size: e.target.value }))}
                  inputProps={{ min: getBulkMinDiskGb() }}
                  error={Number(bulkForm.disk_size) < getBulkMinDiskGb()}
                  helperText={
                    Number(bulkForm.disk_size) < getBulkMinDiskGb()
                      ? `최소 ${getBulkMinDiskGb()}G 이상 (Golden Image 가상 디스크 크기)`
                      : ''
                  }
                  fullWidth
                />
              </Stack>

              <TextField
                fullWidth
                select
                label="네트워크 프로필"
                value={bulkForm.network_profile}
                onChange={e => setBulkForm(f => ({ ...f, network_profile: e.target.value }))}
              >
                <MenuItem value="default">default</MenuItem>
                {bulkNetworks.filter(n => n.name !== 'default').map(n => (
                  <MenuItem key={n.name} value={n.name}>{n.name} ({n.cidr})</MenuItem>
                ))}
              </TextField>

              <Stack direction="row" spacing={2}>
                <TextField
                  label="사용자명"
                  value={bulkForm.username}
                  onChange={e => setBulkForm(f => ({ ...f, username: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="비밀번호"
                  type="password"
                  value={bulkForm.password}
                  onChange={e => setBulkForm(f => ({ ...f, password: e.target.value }))}
                  fullWidth
                />
              </Stack>

              {/* GPU 할당 설정 */}
              <Box>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField
                    label="GPU 할당 수"
                    type="number"
                    value={bulkForm.gpu_count}
                    onChange={e => setBulkForm(f => ({ ...f, gpu_count: e.target.value }))}
                    inputProps={{ min: 0, max: bulkAssignableGPUs.length }}
                    sx={{ width: 140 }}
                    helperText={`일괄할당 가능 GPU: ${bulkAssignableGPUs.length}개`}
                  />
                  <Box sx={{ flex: 1, pt: 1 }}>
                    {Number(bulkForm.gpu_count) > 0 && Number(bulkForm.gpu_count) > bulkAssignableGPUs.length && (
                      <Alert severity="warning" sx={{ py: 0.5 }}>
                        요청 GPU({bulkForm.gpu_count}개) &gt; 가용 GPU({bulkAssignableGPUs.length}개)<br />
                        처음 {bulkAssignableGPUs.length}개 VM에만 GPU가 할당됩니다.
                      </Alert>
                    )}
                    {Number(bulkForm.gpu_count) > 0 && Number(bulkForm.gpu_count) <= bulkAssignableGPUs.length && (
                      <Alert severity="info" sx={{ py: 0.5 }}>
                        처음 {bulkForm.gpu_count}개 VM에 GPU가 순서대로 할당됩니다.
                      </Alert>
                    )}
                    {Number(bulkForm.gpu_count) === 0 && (
                      <Alert severity="info" sx={{ py: 0.5 }}>GPU 없이 생성합니다.</Alert>
                    )}
                  </Box>
                </Stack>
              </Box>

              <TextField
                fullWidth
                select
                label="Cloud-init ISO (선택)"
                value={bulkForm.cloud_init_iso || ''}
                onChange={e => setBulkForm(f => ({ ...f, cloud_init_iso: e.target.value }))}
                helperText="선택 시 해당 ISO의 설정을 기반으로 각 VM에 새 ISO가 생성됩니다. 미선택 시 아래 설정으로 자동 생성됩니다."
              >
                <MenuItem value="">자동 생성 (아래 설정 사용)</MenuItem>
                {bulkCloudInitIsos.map(iso => (
                  <MenuItem key={iso.name} value={iso.name}>
                    {iso.hostname || iso.name}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({iso.name})
                    </Typography>
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                label="SSH 공개키 (선택, 한 줄에 하나씩)"
                multiline
                rows={3}
                value={bulkForm.ssh_authorized_keys || ''}
                onChange={e => setBulkForm(f => ({ ...f, ssh_authorized_keys: e.target.value }))}
                placeholder="ssh-rsa AAAA... user@host"
                helperText="입력하지 않아도 호스트 SSH 키가 자동으로 추가됩니다."
              />

              <Divider />

              {/* 포트 포워딩 설정 */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  포트 포워딩 (선택)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  VM당 지정한 포트가 설정 범위 내 호스트 포트로 자동 할당됩니다. VM 부팅 후 IP 획득 시 자동 적용됩니다.
                </Typography>

                {bulkPortForwards.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                    {bulkPortForwards.map((pf, idx) => (
                      <Chip
                        key={idx}
                        size="small"
                        label={`${pf.protocol.toUpperCase()} :${pf.vm_port}${pf.description ? ` (${pf.description})` : ''}`}
                        onDelete={() => setBulkPortForwards(prev => prev.filter((_, i) => i !== idx))}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}

                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    select
                    label="프로토콜"
                    value={bulkPFInput.protocol}
                    onChange={e => setBulkPFInput(p => ({ ...p, protocol: e.target.value }))}
                    size="small"
                    sx={{ width: 90 }}
                  >
                    <MenuItem value="tcp">TCP</MenuItem>
                    <MenuItem value="udp">UDP</MenuItem>
                  </TextField>
                  <TextField
                    label="VM 포트"
                    type="number"
                    value={bulkPFInput.vm_port}
                    onChange={e => setBulkPFInput(p => ({ ...p, vm_port: e.target.value }))}
                    inputProps={{ min: 1, max: 65535 }}
                    size="small"
                    sx={{ width: 100 }}
                    placeholder="22"
                    onKeyDown={e => e.key === 'Enter' && handleAddPortForward()}
                  />
                  <TextField
                    label="설명 (선택)"
                    value={bulkPFInput.description}
                    onChange={e => setBulkPFInput(p => ({ ...p, description: e.target.value }))}
                    size="small"
                    sx={{ flex: 1 }}
                    placeholder="SSH, HTTP, Jupyter..."
                    onKeyDown={e => e.key === 'Enter' && handleAddPortForward()}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddPortForward}
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{ height: 40, mt: 0.1 }}
                  >
                    추가
                  </Button>
                </Stack>
              </Box>

              {bulkLoading && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    VM 생성 중... (시간이 걸릴 수 있습니다)
                  </Typography>
                  <LinearProgress sx={{ mt: 0.5 }} />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {bulkResults ? (
            <Button onClick={handleBulkClose} variant="contained">닫기</Button>
          ) : (
            <>
              <Button onClick={handleBulkClose} color="inherit" disabled={bulkLoading}>
                취소
              </Button>
              <Button
                onClick={handleBulkSubmit}
                variant="contained"
                color="secondary"
                disabled={bulkLoading || !bulkForm.base_name.trim() || Number(bulkForm.disk_size) < getBulkMinDiskGb()}
                startIcon={bulkLoading ? <CircularProgress size={18} /> : <BulkIcon />}
              >
                {bulkLoading ? '생성 중...' : `VM ${bulkForm.count}개 생성`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Golden Image 삭제 확인</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            "{imageToDelete?.display_name}" Golden Image를 정말로 삭제하시겠습니까?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            파일과 메타데이터가 모두 삭제됩니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} color="inherit">
            취소
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" startIcon={<DeleteIcon />}>
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoldenImagesPage;
